import { useCallback, useMemo } from 'react';
import { useGameStore, useRoomStore } from '@store/index';
import { ChessEngine } from '@services/ChessEngine';
import type { Position } from '@shared/types/chess.types';

export const useChessGame = () => {
  const gameState = useGameStore((state) => state.gameState);
  const selectedSquare = useGameStore((state) => state.selectedSquare);
  const legalMoves = useGameStore((state) => state.legalMoves);
  const selectSquare = useGameStore((state) => state.selectSquare);
  const setLegalMoves = useGameStore((state) => state.setLegalMoves);
  const playerColor = useRoomStore((state) => state.playerColor);
  
  const isMyTurn = useMemo(() => {
    return gameState?.currentTurn === playerColor;
  }, [gameState?.currentTurn, playerColor]);
  
  const handleSquareClick = useCallback(
    (position: Position) => {
      console.log('[useChessGame] handleSquareClick called:', position);
      console.log('[useChessGame] gameState:', !!gameState, 'isMyTurn:', isMyTurn, 'playerColor:', playerColor);
      
      if (!gameState || !isMyTurn) {
        console.log('[useChessGame] Not my turn or no game state');
        return;
      }
      
      const clickedPiece = gameState.board.squares.get(
        ChessEngine.positionToKey(position)
      );
      console.log('[useChessGame] Clicked piece:', clickedPiece);
      
      // If clicking on own piece, select it
      if (clickedPiece && clickedPiece.color === playerColor) {
        console.log('[useChessGame] Selecting piece at:', position);
        selectSquare(position);
        const moves = ChessEngine.getPossibleMoves(gameState, position);
        console.log('[useChessGame] Legal moves:', moves);
        setLegalMoves(moves);
        return;
      }
      
      // If a piece is selected and clicking on a legal move, make the move
      if (selectedSquare && legalMoves.length > 0) {
        const isLegalMove = legalMoves.some(
          (move) => move.file === position.file && move.rank === position.rank
        );
        
        if (isLegalMove) {
          console.log('[useChessGame] Making move from', selectedSquare, 'to', position);
          // Move will be handled by parent component
          return { from: selectedSquare, to: position };
        }
      }
      
      // Otherwise, deselect
      console.log('[useChessGame] Deselecting');
      selectSquare(null);
      setLegalMoves([]);
      return null;
    },
    [gameState, isMyTurn, playerColor, selectedSquare, legalMoves, selectSquare, setLegalMoves]
  );
  
  const getLegalMovesForSquare = useCallback(
    (position: Position): Position[] => {
      if (!gameState || !isMyTurn) return [];
      
      const piece = gameState.board.squares.get(
        ChessEngine.positionToKey(position)
      );
      
      if (!piece || piece.color !== playerColor) return [];
      
      return ChessEngine.getPossibleMoves(gameState, position);
    },
    [gameState, isMyTurn, playerColor]
  );
  
  const isInCheck = useMemo(() => {
    return gameState?.inCheck && gameState.currentTurn === playerColor;
  }, [gameState?.inCheck, gameState?.currentTurn, playerColor]);
  
  const getKingPosition = useCallback((): Position | null => {
    if (!gameState || !playerColor) return null;
    
    for (const [key, piece] of gameState.board.squares) {
      if (piece.type === 'king' && piece.color === playerColor) {
        return ChessEngine.keyToPosition(key);
      }
    }
    
    return null;
  }, [gameState, playerColor]);
  
  return {
    gameState,
    isMyTurn,
    selectedSquare,
    legalMoves,
    handleSquareClick,
    getLegalMovesForSquare,
    isInCheck,
    getKingPosition,
  };
};